import React, { useState, useEffect } from "react";
import { Select, Upload, Button, Table, message, Row, Col } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import dataService from "../networks/api/service/dataService";

const { Option } = Select;
const { Dragger } = Upload;

const PastAnalysis = () => {
  const [constituencyList, setConstituencyList] = useState([]);
  const [assemblyList, setAssemblyList] = useState([]);
  const [constituencyId, setConstituencyId] = useState(null);
  const [assemblyId, setAssemblyId] = useState(null);
  const [fileData, setFileData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [payload, setPayload] = useState(null);
  const [year, setYear] = useState(null);
  const [electionType, setelectionType] = useState(null);
   const [uploadedData, setUploadedData] = useState([]);

  const OnYearSelect = (value) => setYear(value);
  const OnElectionTypeSelect = (value) => setelectionType(value);

  const getConstituency = async () => {
    try {
      const res = await dataService.getAllConstituency();
      if (res.code === 200){ setConstituencyList(res.data);}
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    getConstituency();
  }, []);

  const OnConstituencySelect = async (value) => {
    setConstituencyId(value);
    setAssemblyId("");
    setAssemblyList([]);
    setYear(null);
    setelectionType(null);

    try {
      const res = await dataService.getAssemblyFromConstituency(value);
      if (res.code === 200) setAssemblyList(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const OnAssemblySelect = (value) => {
    setAssemblyId(value);
    setYear(null);
    setelectionType(null);
  };

  const handleFileUpload = (info) => {
    const { status } = info.file;
    if (status !== "uploading") {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        
        // Extract header and rows
        const [headerRow, ...rows] = jsonData;

        // Process rows and ignore color column
        const processedData = rows
          .filter((row) => row.length > 1 && row[1]) // Ensure non-empty "Party" column
          .filter((row) => !/hold/i.test(row[1])) // Exclude rows with "Hold" in the Party column
          .map((row) => ({
            Party: row[1], // Extract Party from column B
            Candidate: row[2] || "", // Candidate data from column C
            Votes: parseInt(row[3], 10) || 0, // Votes data from column D
            "%": row[4] || "0", // Percentage data from column E
            "±%": row[5] || "0", // ±% data from column F
          }));

        // Extract majority and turnout values based on row data
        let majority = 0;
        let turnout = 0;

        // Iterate through rows to find majority and turnout under the Votes column
        rows.forEach((row) => {
          if (/majority/i.test(row[0])) {
            majority = parseInt(row[3], 10) || 0; // Extract majority value from Votes column (column D)
          }
          if (/turnout/i.test(row[0])) {
            turnout = parseInt(row[3], 10) || 0; // Extract turnout value from Votes column (column D)
          }
        });

        // Create dynamic columns
        const dynamicColumns = [
          { title: "Party", dataIndex: "Party", key: "Party" },
          { title: "Candidate", dataIndex: "Candidate", key: "Candidate" },
          { title: "Votes", dataIndex: "Votes", key: "Votes" },
          { title: "%", dataIndex: "%", key: "%" },
          { title: "±%", dataIndex: "±%", key: "±%" },
        ];

        // Update state with processed data
        if (processedData.length) {
          setColumns(dynamicColumns);
          setFileData(processedData);
          message.success("File uploaded and processed successfully!");
          // Set the payload with majority and turnout
          setPayload({
            majority: majority,
            turnout: turnout,
            pastElectionDetails: processedData,
          });
          
          
        } else {
          message.error("No valid data found in the uploaded file.");
        }
        
      };
      reader.readAsArrayBuffer(info.file.originFileObj);
    }
  };

  const handleGeneratePayload = async () => {
    if (!constituencyId || !assemblyId || !fileData.length) {
      message.error("Please select all fields and upload the file.");
      return;
    }

    const data = {
      ...payload,
      typeOfElection: electionType,
      year: year,
    };

    try {
      const response = await dataService.uploadPastDetails(data, assemblyId);
      if (response.code === 200) {
        message.success("File Uploaded successfully!");
      }
      setUploadedData([]);
          setColumns([]);
    } catch (e) {}
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>Past Election Analysis</h2>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Select
            value={constituencyId}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => {
              const children = option?.children;
              return String(children)
                .toLowerCase()
                .includes(input.toLowerCase());
            }}
            placeholder="Select Parliament"
            style={{
              width: "100%",
              border: "1px solid grey",
              borderRadius: 5,
            }}
            onChange={OnConstituencySelect}
          >
            <Option key={827272} value="" disabled>
              Select Parliament
            </Option>
            {constituencyList.map((item) => (
              <Option key={item._id} value={item._id}>
                {item.constituencyName}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Select
            value={assemblyId}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => {
              const children = option?.children;
              return String(children)
                .toLowerCase()
                .includes(input.toLowerCase());
            }}
            placeholder="Select Assembly"
            style={{
              width: "100%",
              border: "1px solid grey",
              borderRadius: 5,
            }}
            onChange={OnAssemblySelect}
          >
            <Option key={827272} value="" disabled>
              Select Assembly
            </Option>
            {assemblyList.map((item) => (
              <Option key={item._id} value={item._id}>
                {item.assemblyName}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Select
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => {
              const children = option?.children;
              return String(children)
                .toLowerCase()
                .includes(input.toLowerCase());
            }}
            placeholder="Select Year"
            style={{
              width: "100%",
              border: "1px solid grey",
              borderRadius: 5,
            }}
            value={year}
            onChange={OnYearSelect}
          >
            <Option key={827272} value="" disabled>
              Select Year
            </Option>
            {[2024, 2019, 2014].map((y) => (
              <Option key={y} value={y}>
                {y}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Select
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => {
              const children = option?.children;
              return String(children)
                .toLowerCase()
                .includes(input.toLowerCase());
            }}
            placeholder="Select Election Type"
            style={{
              width: "100%",
              border: "1px solid grey",
              borderRadius: 5,
            }}
            onChange={OnElectionTypeSelect}
            value={electionType}
          >
            <Option key={827272} value="" disabled>
              Election Type
            </Option>
            <Option value="AC">AC</Option>
            <Option value="PC">PC</Option>
          </Select>
        </Col>
      </Row>

      <div style={{ marginBottom: "20px" }}>
        <Dragger
          accept=".xlsx, .xls"
          customRequest={({ file, onSuccess }) => {
            setTimeout(() => onSuccess("ok"), 0);
          }}
          onChange={handleFileUpload}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag file to upload</p>
          <p className="ant-upload-hint">
            Upload an Excel file (.xlsx or .xls) with election details.
          </p>
        </Dragger>
      </div>

      <div style={{ marginBottom: "20px" }}>
        <Button type="primary" onClick={handleGeneratePayload}>
          Upload Data
        </Button>
      </div>

      <Table
        bordered
        columns={columns}
        dataSource={fileData}
        pagination={true}
        scroll={{ x: true }}
      />
    </div>
  );
};

export default PastAnalysis;
